import {
    Component,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
    ConfirmationDialogComponent,
    ConfirmationDialogOption,
    ConfirmationDialogType,
} from '@components/confirmation-dialog/confirmation-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { MessageDialogService } from '@service/message-dialog.service';
import { OutputSettingsSocketService } from '@service/output-settings-socket.service';
import { OutputSettingsService } from '@service/output-settings.service';
import { Channel } from 'icreate/common/models/channel';
import { isAnalog } from 'icreate/common/models/channel-type';
import { OutputSetting, OutputSettingChannel } from 'icreate/common/models/output-settings';
import { SensorTagSlot } from 'icreate/common/models/sensor-cable-tag';
import { Vessel } from 'icreate/common/models/vessel';
import * as _ from 'lodash';
import { lastValueFrom, Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'app-analog-output-settings-table',
    templateUrl: './analog-output-settings-table.component.html',
    styleUrl: './analog-output-settings-table.component.css',
    standalone: false,
})
export class AnalogOutputSettingsTableComponent implements OnInit, OnChanges, OnDestroy {
    readonly AnalogSettingsLength = 8;
    @Input() selectedSlot: SensorTagSlot;
    @Input() channels: Channel[];
    @Input() vessel: Vessel;
    @Output() onCancel: EventEmitter<void> = new EventEmitter();
    outputSettings: OutputSetting[];
    initialSettings: OutputSetting[];
    isLoadingTerminals: boolean = false;
    isErrorMessageVisible: OutputSetting;
    showOptions: boolean = false;

    settingsErrorSet: Set<number> = new Set();

    private destroy$: Subject<void> = new Subject();

    @HostListener('document:click', ['$event'])
    clickOut(event: any) {
        this.isErrorMessageVisible = null;
        this.showOptions = false;
    }

    constructor(
        private translate: TranslateService,
        private outputSettingsService: OutputSettingsService,
        private outputSettingsSocket: OutputSettingsSocketService,
        private messageDialogService: MessageDialogService,
        private dialog: MatDialog
    ) {}

    ngOnInit(): void {
        this.channels = this.channels.filter(channel => isAnalog(channel?.fcuData?.channelType));

        this.outputSettingsSocket
            .analogSettingsUpdated$()
            .pipe(takeUntil(this.destroy$))
            .subscribe(({ settings, vesselId }: { settings: OutputSetting[]; vesselId: string }) => {
                if (this.vessel.id == vesselId) {
                    const slotId = settings?.[0]?.slotId;
                    if (slotId && slotId == this.selectedSlot.slotId) {
                        this.outputSettings = settings;
                        this.showUpdatedMessage();
                    }
                }
            });
    }

    async ngOnChanges(changes: SimpleChanges): Promise<void> {
        if (changes.selectedSlot && this.selectedSlot) {
            this.isLoadingTerminals = true;
            try {
                const settings = await this.outputSettingsService.getAllAOTerminals(this.selectedSlot.slotId);
                const settingsMap = new Map<number, OutputSetting>();
                settings.forEach(setting => settingsMap.set(setting.terminalNum, setting));
                this.outputSettings = Array.from({ length: this.AnalogSettingsLength }, (v, i) => {
                    const terminalNum = i + 1;
                    if (settingsMap.has(terminalNum)) return settingsMap.get(terminalNum);
                    return {
                        vesselId: this.selectedSlot.vesselId,
                        slotId: this.selectedSlot.slotId,
                        terminalNum: i + 1,
                        channels: [],
                        outputMode: null,
                        signalType: null,
                    };
                });
                this.initialSettings = _.cloneDeep(this.outputSettings);
            } catch (err) {
                console.error(err);
            }
            this.isLoadingTerminals = false;
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    getActiveTerminalsText() {
        if (!this.selectedSlot || this.isLoadingTerminals) return;
        const activeTerminals = this.outputSettings.filter(setting => setting.channels.length > 0).length;
        return this.translate.instant('output-settings.active-terminals', {
            active: `${activeTerminals}/8`,
        });
    }

    getSlotTileArray() {
        return this.outputSettingsService.getSlotTileArray(this.selectedSlot);
    }

    isSaveEnabled() {
        if (this.settingsErrorSet.size > 0) return false;
        return !_.isEqual(this.initialSettings, this.outputSettings);
    }

    async removeChannel(setting: OutputSetting) {
        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            width: '415px',
            data: {
                type: ConfirmationDialogType.SIMPLE,
                message: 'output-settings.AO.clear.message',
                message2: 'output-settings.AO.clear.message-2',
                discardButton: 'output-settings.AO.clear.cancel',
                saveButton: 'output-settings.AO.clear.confirm',
            },
        });

        const confirmationOption = await lastValueFrom(dialogRef.afterClosed());

        if (confirmationOption == ConfirmationDialogOption.SAVE) setting.channels = [];
    }

    handleChannelSelected(channel: Channel, setting: OutputSetting) {
        setting.channels = [{ channelId: channel.id, channel: channel, settingId: setting.id }];
    }

    handleSettingError(hasError: boolean, setting: OutputSetting) {
        if (hasError) this.settingsErrorSet.add(setting.terminalNum);
        else this.settingsErrorSet.delete(setting.terminalNum);
    }

    getChannelName(settingChannel: OutputSettingChannel) {
        const channel = settingChannel.channel;
        return `${channel.fcuData.fcuChannelId} ${channel.opsData.name}`;
    }

    getStatusString(setting: OutputSetting) {
        if (this.settingsErrorSet.has(setting.terminalNum)) return 'output-settings.AO.table.status.invalid';
        else if (setting.channels[0]) return 'output-settings.AO.table.status.active';
        else return 'output-settings.AO.table.status.empty';
    }

    async save() {
        if (!this.isSaveEnabled()) return;
        this.isLoadingTerminals = true;
        try {
            await this.outputSettingsService.saveAOTerminals(this.outputSettings, this.vessel.id);
            this.initialSettings = _.cloneDeep(this.outputSettings);
            this.showUpdatedMessage();
        } catch (err) {
            console.error(err);
        }
        this.isLoadingTerminals = false;
    }

    async cancel() {
        const shouldLeave = await this.shouldLeaveConfirmation();
        if (!shouldLeave) return;
        this.onCancel.emit();
    }

    async shouldLeaveConfirmation() {
        let shouldLeave = true;
        if (!_.isEqual(this.initialSettings, this.outputSettings)) {
            const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
                width: '415px',
                data: {
                    type: ConfirmationDialogType.COMPLEX,
                    title: 'output-settings.AO.cancel.title',
                    backButton: 'output-settings.AO.cancel.cancel',
                    discardButton: 'output-settings.AO.cancel.discard',
                    saveButton: 'output-settings.AO.cancel.save',
                    redDiscard: true,
                    saveDisabled: !this.isSaveEnabled(),
                },
            });

            const confirmationOption = await lastValueFrom(dialogRef.afterClosed());

            if (confirmationOption == ConfirmationDialogOption.GO_BACK) shouldLeave = false;
            else if (confirmationOption == ConfirmationDialogOption.SAVE) {
                if (this.isSaveEnabled()) await this.save();
            }
        }

        return shouldLeave;
    }

    showUpdatedMessage() {
        this.messageDialogService.openMessage('output-settings.AO.notification.updated', {
            num: this.outputSettingsService.paddNumber(this.selectedSlot.no),
        });
    }

    async deleteAllChannels() {
        if (this.outputSettings.filter(setting => setting.channels?.length > 0)?.length == 0) {
            return;
        }
        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            width: '415px',
            data: {
                type: ConfirmationDialogType.SIMPLE,
                title: 'output-settings.AO.clear-all.title',
                message: 'output-settings.AO.clear-all.message',
                message2: 'output-settings.AO.clear-all.message-2',
                discardButton: 'output-settings.AO.clear-all.cancel',
                saveButton: 'output-settings.AO.clear-all.confirm',
                width: '452px',
            },
        });

        const confirmationOption = await lastValueFrom(dialogRef.afterClosed());

        if (confirmationOption == ConfirmationDialogOption.SAVE) {
            this.outputSettings = this.outputSettings.map(setting => {
                setting.channels = [];
                return setting;
            });
        }
    }
}
